import { BookOpen, LayoutDashboard, Sparkles } from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { todayHref } from "@/lib/today-position";

export type NavIcon = ComponentType<SVGProps<SVGSVGElement>>;

export type NavLink = {
  href: string;
  label: string;
  icon: NavIcon;
  chip?: string;
};

export type NavSection = {
  id: string;
  label: string;
  icon: NavIcon;
  items: NavLink[];
};

export type NavConfig = {
  pins: NavLink[];
  sections: NavSection[];
  footer: NavLink[];
};

/**
 * Pins are the work: Today and the Assistant. Docs sits in the footer —
 * reachable from every screen without competing with the queue for attention.
 * Settings is reached from the profile menu.
 */
export function getNavConfig(): NavConfig {
  return {
    pins: [
      { href: todayHref(), label: "Today", icon: LayoutDashboard },
      { href: "/ai-assistant", label: "AI Assistant", icon: Sparkles },
    ],
    sections: [],
    footer: [{ href: "/docs", label: "Docs", icon: BookOpen }],
  };
}

/** @deprecated Prefer getNavConfig() so Today restores the last queue position. */
export const NAV_CONFIG: NavConfig = {
  pins: [
    { href: "/", label: "Today", icon: LayoutDashboard },
    { href: "/ai-assistant", label: "AI Assistant", icon: Sparkles },
  ],
  sections: [],
  footer: [{ href: "/docs", label: "Docs", icon: BookOpen }],
};

const EXPANDED_KEY = "ceo-cmd.nav.expanded";
const COLLAPSED_KEY = "ceo-cmd.nav.collapsed";

function parseHref(href: string): { pathname: string; search: string } {
  const q = href.indexOf("?");
  if (q === -1) return { pathname: href, search: "" };
  return { pathname: href.slice(0, q), search: href.slice(q) };
}

const PATH_ALIASES: Record<string, string[]> = {
  "/": ["/"],
  "/ai-assistant": ["/ai-assistant"],
  "/docs": ["/docs"],
  "/settings/account": ["/settings"],
};

export function isActivePath(
  href: string,
  pathname: string,
  _search = "",
): boolean {
  const { pathname: targetPath } = parseHref(href);

  if (targetPath === "/") return pathname === "/";

  if (targetPath === "/settings/account") {
    return pathname.startsWith("/settings");
  }

  const aliases = PATH_ALIASES[targetPath] ?? [targetPath];
  return aliases.some((alias) => {
    if (alias === "/") return pathname === "/";
    return pathname === alias || pathname.startsWith(`${alias}/`);
  });
}

export function sectionContainsPath(
  section: NavSection,
  pathname: string,
  search = "",
): boolean {
  return section.items.some((item) => isActivePath(item.href, pathname, search));
}

export function sectionIdForPath(pathname: string, search = ""): string | null {
  for (const section of NAV_CONFIG.sections) {
    if (sectionContainsPath(section, pathname, search)) return section.id;
  }
  return null;
}

export function loadExpandedSections(pathname: string, search = ""): Set<string> {
  const active = sectionIdForPath(pathname, search);
  let stored: string[] = [];
  try {
    const raw = localStorage.getItem(EXPANDED_KEY);
    if (raw) stored = JSON.parse(raw) as string[];
  } catch {
    /* ignore */
  }
  const next = new Set(
    stored.length > 0 ? stored : NAV_CONFIG.sections.map((s) => s.id),
  );
  if (active) next.add(active);
  return next;
}

export function persistExpandedSections(ids: Set<string>): void {
  try {
    localStorage.setItem(EXPANDED_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

export function loadNavCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function persistNavCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    /* ignore */
  }
}
